import { wordCount } from '@concord/domain';
import { type AssistantSuggestionView, MAX_QUESTIONS } from '@concord/domain/contracts';
import {
  AssistantSuggestion,
  Button,
  HistoryEditor,
  historyStyles,
  Notice,
  QCard,
  QuestionList,
  Saved,
  WizardNav,
} from '@concord/ui';
import { useEffect, useRef, useState } from 'react';
import { errorMessage } from '../../../lib/api';
import { askAssistant, resolveSuggestion, useAutosave, usePatchIntake } from '../api';
import type { StepProps } from '../IntakeWizard';

/** INT-05 to INT-07: the history, the numbered questions, and the writing assistant. */
export function HistoryStep({ v, back, next, mode }: StepProps) {
  const history = useAutosave(v.id, 'history', v.history);
  const patch = usePatchIntake(v.id);
  const [questions, setQuestions] = useState<string[]>(v.questions);
  const [suggestion, setSuggestion] = useState<AssistantSuggestionView | null>(null);
  const [asking, setAsking] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const qTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const saveQuestions = (next: string[]) => {
    setQuestions(next);
    if (qTimer.current) clearTimeout(qTimer.current);
    qTimer.current = setTimeout(
      () => patch.mutate({ questions: next.map((q) => q.trim()).filter(Boolean) }),
      600,
    );
  };
  useEffect(
    () => () => {
      if (qTimer.current) clearTimeout(qTimer.current);
    },
    [],
  );

  async function help() {
    setAsking(true);
    setNote(null);
    try {
      const s = await askAssistant(v.id, history.value);
      setSuggestion(s);
      if (!s) setNote('Nothing to suggest right now. Your history reads clearly.');
    } catch (e) {
      setNote(errorMessage(e));
    } finally {
      setAsking(false);
    }
  }

  function insert(addition: string, anchor: string | null) {
    const text = history.value;
    if (anchor && text.includes(anchor)) {
      const at = text.indexOf(anchor) + anchor.length;
      history.change(`${text.slice(0, at)} ${addition}${text.slice(at)}`);
    } else {
      history.change(text.trim() ? `${text.trimEnd()}\n\n${addition}` : addition);
    }
  }

  async function resolve(outcome: 'accepted' | 'edited' | 'skipped') {
    if (!suggestion) return;
    if (outcome !== 'skipped' && suggestion.addition) insert(suggestion.addition, suggestion.anchor);
    if (outcome !== 'skipped' && suggestion.questions.length) {
      saveQuestions([...questions, ...suggestion.questions].slice(0, MAX_QUESTIONS));
    }
    const sid = suggestion.id;
    setSuggestion(null);
    await resolveSuggestion(v.id, sid, outcome).catch(() => undefined);
    if (outcome === 'edited') {
      requestAnimationFrame(() => document.querySelector<HTMLTextAreaElement>('textarea')?.focus());
    }
  }

  const words = wordCount(history.value);
  const saved =
    history.state.kind === 'saving'
      ? 'Saving…'
      : history.state.kind === 'error'
        ? 'Not saved yet, retrying'
        : history.state.kind === 'saved' || v.historySavedAt
          ? 'Saved just now'
          : 'Saves as you type';
  const ready = history.value.trim().length > 0 && questions.some((q) => q.trim());

  return (
    <>
      <QCard
        label="Your history"
        title={
          mode === 'staff'
            ? 'The diagnosis and treatment so far, as the patient tells it'
            : 'Tell the physicians about your diagnosis and treatment so far'
        }
        lead="Write it the way you would tell a friend. Dates, test results and the names of treatments help most. There is no length limit, and it saves as you type."
      >
        <HistoryEditor
          label={mode === 'staff' ? 'The patient’s history' : 'Your history'}
          value={history.value}
          onChange={history.change}
          saved={saved}
          words={words}
          onHelp={help}
          helpBusy={asking}
        />
        {suggestion && (
          <AssistantSuggestion
            observation={suggestion.observation}
            addition={suggestion.addition}
            anchor={suggestion.anchor}
            onAccept={() => resolve('accepted')}
            onEdit={() => resolve('edited')}
            onSkip={() => resolve('skipped')}
          />
        )}
        {note && <Notice>{note}</Notice>}
      </QCard>

      <QCard
        label="Questions for the panel"
        title="What do you want the physicians to answer?"
        lead="Numbered so each one gets answered. Two or three clear questions work better than a long list."
      >
        <QuestionList questions={questions} onChange={saveQuestions} max={MAX_QUESTIONS} />
        <div className={historyStyles.qhint}>
          The assistant can suggest questions from the history. Any suggestion is shown first.
        </div>
      </QCard>

      <WizardNav
        back={
          <Button
            variant="sec"
            onClick={async () => {
              await history.flush();
              back();
            }}
          >
            Back to about {mode === 'staff' ? 'the patient' : 'you'}
          </Button>
        }
        status={<Saved>Saved. Come back any time, from any device.</Saved>}
        next={
          <Button
            size="lg"
            disabled={!ready}
            onClick={async () => {
              await history.flush();
              if (qTimer.current) {
                clearTimeout(qTimer.current);
                await patch.mutateAsync({ questions: questions.map((q) => q.trim()).filter(Boolean) });
              }
              next();
            }}
          >
            Continue to {mode === 'staff' ? 'records' : 'your records'}
          </Button>
        }
      />
    </>
  );
}
